import type { Highlight } from '@akem/shared';
import { PauseCircleOutline, PlayCircleOutline } from '@mui/icons-material';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import {
  marketService,
  type HighlightWithInsight,
} from '../../../services/marketService.js';
import { getProfileHighlights } from '../../../services/profile.js';
import { mockHighlights } from '../../market/mockData.js';
import type { WidgetProps } from '../types/index.js';
import { useProfileNavigation } from '../hooks/useProfileNavigation.js';

type EnrichedHighlight = HighlightWithInsight;

const directionChip = {
  [-1]: { label: 'Negative', color: 'error' },
  [0]: { label: 'Neutral', color: 'default' },
  [1]: { label: 'Positive', color: 'success' },
} satisfies Record<Highlight['direction'], { label: string; color: 'success' | 'default' | 'error' }>;

const AUTO_ROTATE_MS = 8000;

export const AkemTickerWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs, onUpdateState }) => {
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const [highlights, setHighlights] = React.useState<EnrichedHighlight[]>([]);
  const { openProfileByTicker } = useProfileNavigation();
  const allowMocks = React.useMemo(
    () => /^(1|true|yes)$/i.test(String(import.meta.env.VITE_ALLOW_MARKET_MOCKS ?? '')),
    []
  );
  const selectedHighlight = React.useMemo(() => {
    return widget.outputs.find((output) => output.id === 'highlightSelected')?.value as EnrichedHighlight | undefined;
  }, [widget.outputs]);

  const [activeIndex, setActiveIndex] = React.useState(() => {
    if (selectedHighlight) {
      const idx = highlights.findIndex((item) => item.id === selectedHighlight.id);
      if (idx >= 0) return idx;
    }
    return 0;
  });
  const [autoRotate, setAutoRotate] = React.useState(true);
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  const activeHighlight = highlights[activeIndex];

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = profileId
          ? ((await getProfileHighlights(profileId).catch(() => [])) as HighlightWithInsight[])
          : await marketService.getMarketHighlights().catch(() => []);
        if (!cancelled) {
          setHighlights(data);
          setActiveIndex(0);
          onUpdateState({ loading: false, data });
        }
        return;
      } catch (error) {
        if (!cancelled) {
          console.warn('[AkemTicker] failed to load highlights', error);
        }
      }
      if (!cancelled) {
        if (allowMocks) {
          setHighlights(mockHighlights as EnrichedHighlight[]);
          setActiveIndex(0);
          onUpdateState({ loading: false, data: mockHighlights });
        } else {
          setHighlights([]);
          setActiveIndex(0);
          onUpdateState({ loading: false, data: [] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowMocks, profileId]);

  React.useEffect(() => {
    if (highlights.length === 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((prev) => {
      if (prev < highlights.length) return prev;
      return 0;
    });
  }, [highlights.length]);

  React.useEffect(() => {
    if (!selectedHighlight) return;

    const idx = highlights.findIndex((item) => item.id === selectedHighlight.id);
    if (idx >= 0 && idx !== activeIndex) {
      setActiveIndex(idx);
    }
  }, [activeIndex, selectedHighlight]);

  const highlightCount = highlights.length;

  const stopPropagation = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  React.useEffect(() => {
    if (!autoRotate || highlightCount === 0) return;
    const timer = window.setInterval(() => {
      // Compute next index explicitly so we can log it for debugging
      setActiveIndex((prev) => {
        const next = (prev + 1) % highlightCount;
        // Debug log to correlate potential jank with ticker rotation
        // This runs roughly every 8s when the market canvas is open
        try {
          // eslint-disable-next-line no-console
          console.log(
            `[AkemTicker] auto-rotate tick -> index ${next} at ${new Date().toISOString()}`
          );
        } catch {}
        return next;
      });
    }, AUTO_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [autoRotate, highlightCount]);

  React.useEffect(() => {
    if (!activeHighlight) return;

    // Suppress emissions during auto-rotate to avoid unnecessary canvas-wide updates
    if (autoRotate) {
      try {
        // eslint-disable-next-line no-console
        console.debug('[AkemTicker] suppressing output emit during auto-rotate');
      } catch {}
      return;
    }

    if (!selectedHighlight || activeHighlight.id !== selectedHighlight.id) {
      onUpdateOutputs({ highlightSelected: activeHighlight });
    }
  }, [activeHighlight, autoRotate, onUpdateOutputs, selectedHighlight]);

  const timeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    []
  );

  // Marquee-style ticker: continuously cycling cards
  const tickerItems: EnrichedHighlight[] = React.useMemo(() => {
    return highlights.length > 0 ? [...highlights, ...highlights] : [];
  }, [highlights]);

  if (tickerItems.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">No highlights available.</Typography>
      </Box>
    );
  }

  // Duration scales with item count to keep speed reasonable
  const DURATION_PER_ITEM = 4; // seconds per card
  const animationDuration = Math.max(16, Math.round((highlights.length || 4) * DURATION_PER_ITEM));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Scrolling track with overlayed control */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          overflow: 'hidden',
          px: 0, // start cards flush with the left edge
          '&:hover .ticker-track': { animationPlayState: 'paused' },
        }}
      >
        {/* Overlay pause/resume so it doesn't reserve vertical space */}
        <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}>
          <Tooltip title={autoRotate ? 'Pause ticker' : 'Resume ticker'}>
            <IconButton size="small" onClick={() => setAutoRotate((prev) => !prev)}>
              {autoRotate ? <PauseCircleOutline /> : <PlayCircleOutline />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          className="ticker-track"
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.25,
            width: 'max-content',
            ml: 0, // no leading margin
            pl: 0, // no leading padding
            animation: `${animationDuration}s linear infinite tickerScroll`,
            animationPlayState: autoRotate ? 'running' : 'paused',
            '@keyframes tickerScroll': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' },
            },
          }}
        >
          {tickerItems.map((h, idx) => {
            const origIndex = highlights.length ? (idx % highlights.length) : 0;
            const isActive = highlights[activeIndex]?.id === h.id;
            const isHovered = hoveredId === h.id;
            const dirMeta = directionChip[h.direction];
            const insight = (h as EnrichedHighlight).insight;
            const summaryText = insight?.summary ?? h.summary;
            const actions = insight?.recommendedActions ?? [];
            const catalysts = insight?.catalysts ?? [];
            const risks = insight?.risks ?? [];
            const supportingData = insight?.supportingData ?? [];
            const hasTooltipContent = Boolean(
              summaryText ||
                actions.length ||
                catalysts.length ||
                risks.length ||
                supportingData.length ||
                h.link
            );
            const tooltipContent = hasTooltipContent ? (
              <Box sx={{ maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {summaryText && (
                  <Typography variant="body2" color="text.primary">
                    {summaryText}
                  </Typography>
                )}
                {actions.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Recommended actions
                    </Typography>
                    <Stack spacing={0.5} mt={0.25}>
                      {actions.map((action, idx) => (
                        <Typography variant="caption" color="text.primary" key={`${h.id}-action-${idx}`}>
                          • {action}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
                {catalysts.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Catalysts
                    </Typography>
                    <Stack spacing={0.5} mt={0.25}>
                      {catalysts.map((item, idx) => (
                        <Typography variant="caption" color="text.primary" key={`${h.id}-catalyst-${idx}`}>
                          • {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
                {risks.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Risks to monitor
                    </Typography>
                    <Stack spacing={0.5} mt={0.25}>
                      {risks.map((item, idx) => (
                        <Typography variant="caption" color="text.primary" key={`${h.id}-risk-${idx}`}>
                          • {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
                {supportingData.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Supporting data
                    </Typography>
                    <Stack spacing={0.5} mt={0.25}>
                      {supportingData.map((item, idx) => (
                        <Typography variant="caption" color="text.primary" key={`${h.id}-support-${idx}`}>
                          • {item.label}: {item.value}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
                {h.link && (
                  <Link
                    href={h.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    color="primary"
                    underline="hover"
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                    onClick={(event) => event.stopPropagation()}
                  >
                    Open source article
                  </Link>
                )}
              </Box>
            ) : null;
            return (
              <Tooltip
                key={`${h.id}-${idx}`}
                title={tooltipContent ?? ''}
                placement="top"
                arrow
                enterDelay={200}
                leaveDelay={100}
                disableHoverListener={!hasTooltipContent}
                disableFocusListener={!hasTooltipContent}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      boxShadow: 4,
                      p: 1.5,
                      borderRadius: 1,
                      maxWidth: 360,
                    },
                  },
                  arrow: {
                    sx: { color: 'background.paper' },
                  },
                }}
              >
                <Box
                  onClick={() => {
                    setAutoRotate(false);
                    if (origIndex >= 0) setActiveIndex(origIndex);
                    onUpdateOutputs({ highlightSelected: h });
                  }}
                  component="button"
                  type="button"
                  sx={{
                    minWidth: 240,
                    maxWidth: 320,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor:
                      isActive || isHovered ? 'primary.main' : 'divider',
                    p: 1.25,
                    cursor: 'pointer',
                    bgcolor: isActive ? 'action.selected' : 'background.paper',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    color: 'inherit',
                    backgroundImage: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    '&:hover': { borderColor: 'primary.light' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                    },
                  }}
                  onMouseEnter={() => setHoveredId(h.id)}
                  onMouseLeave={() => setHoveredId((prev) => (prev === h.id ? null : prev))}
                  onFocus={() => setHoveredId(h.id)}
                  onBlur={() => setHoveredId((prev) => (prev === h.id ? null : prev))}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={dirMeta.label} color={dirMeta.color} size="small" />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {timeFormatter.format(new Date(h.ts))}
                    </Typography>
                  </Stack>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {h.title}
                </Typography>
                {insight && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`Impact ${(insight.impactScore * 100).toFixed(0)}%`}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Confidence: {insight.confidence}
                    </Typography>
                  </Stack>
                )}
                {h.relatedSymbols?.length ? (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {h.relatedSymbols.map((symbol) => (
                        <Chip
                          key={`${h.id}-${symbol}`}
                        label={symbol}
                        size="small"
                        variant="outlined"
                        onMouseDown={stopPropagation}
                        onPointerDown={stopPropagation}
                        onClick={(event) => {
                          event.stopPropagation();
                          setAutoRotate(false);
                          if (!selectedHighlight || selectedHighlight.id !== h.id) {
                            onUpdateOutputs({ highlightSelected: h });
                          }
                          openProfileByTicker(symbol);
                        }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
